import React, { useState, useEffect, useRef } from "react";

/* ============================================================
   RELAYABLE — Simulador público de valoración (vendedor)
   ============================================================
   Arquitectura, identidad visual y patrones de UX confirmados
   con Rubén Merino García (RM Estrategia) en sesión de diseño.
   Reutiliza exactamente la paleta, tipografía y patrones de
   interacción del simulador de viabilidad ya construido.

   Versión de producción: textos V1/V2/V3, teaser SL, subtítulo del CTA
   SL y conexión a backend (URL + token) ya son contenido/datos
   definitivos, no placeholders.

   PENDIENTE — fuera del alcance de este archivo (vive en el backend):
   - ID_PDF_GUIA_SL y la función enviarGuiaSL() en el script de Apps
     Script (ver TODO junto a enviarLead más abajo).
   - Texto del email automático (asunto + cuerpo) que dispara ese script.
   ============================================================ */

/* ----------------------- IDENTIDAD VISUAL ----------------------- */

const C = {
  carbon: "#1C1A17",
  ocre: "#B8860B",
  ocreDark: "#9A7009",
  ocreLight: "#F7F0DC",
  green: "#2A7A52",
  greenLight: "#EBF5F0",
  greenDark: "#1E5A3C",
  red: "#C14F4F",
  redLight: "#F9EBEB",
  cream: "#FAF8F4",
  creamDark: "#EEE8DE",
  stone: "#7A776F",
  stoneLight: "#B0ADA5",
  border: "rgba(28,26,23,0.09)",
  borderMid: "rgba(28,26,23,0.15)",
  white: "#FFFFFF",
};

const FONT_DISPLAY = "'Playfair Display', Georgia, serif";
const FONT_BODY = "'DM Sans', system-ui, sans-serif";
const FONTS_LINK =
  "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;1,400&family=DM+Sans:wght@300;400;500;600;700&display=swap";

function CargarFuentes() {
  return <link rel="stylesheet" href={FONTS_LINK} />;
}

function Logo() {
  return (
    <span style={{ fontFamily: FONT_DISPLAY, fontSize: 19, fontWeight: 500, color: C.carbon }}>
      relay<span style={{ color: C.ocre }}>able</span>
    </span>
  );
}

/* ----------------------- BENCHMARKS SECTORIALES ----------------------- */

const SECTORES = {
  "Hostelería": { multiploBeneficio: [1.5, 3.0], margenBruto: [0.55, 0.70] },
  "Alimentación": { multiploBeneficio: [1.0, 2.5], margenBruto: [0.30, 0.45] },
  "Comercio / Retail": { multiploBeneficio: [1.0, 2.5], margenBruto: [0.35, 0.55] },
  "Peluquería y estética": { multiploBeneficio: [1.5, 3.0], margenBruto: [0.55, 0.70] },
  "Servicios profesionales": { multiploBeneficio: [1.5, 4.0], margenBruto: [0.50, 0.75] },
  "Servicios a empresas": { multiploBeneficio: [1.5, 3.0], margenBruto: [0.25, 0.45] },
  "Salud / Bienestar": { multiploBeneficio: [1.5, 3.5], margenBruto: [0.55, 0.72] },
  "Industria / Taller": { multiploBeneficio: [1.0, 2.5], margenBruto: [0.35, 0.55] },
  "Otro": { multiploBeneficio: [1.0, 3.0], margenBruto: [0.40, 0.65] },
};

const mid = (range) => (range[0] + range[1]) / 2;

/* Factor de ventas derivado: margen bruto medio del sector ÷ múltiplo de
   beneficio medio del sector (decisión cerrada con Rubén). */
function factorVentas(sector) {
  const s = SECTORES[sector] || SECTORES["Otro"];
  return mid(s.margenBruto) / mid(s.multiploBeneficio);
}

/* ----------------------- MOTOR DE CÁLCULO ----------------------- */

/*
  Tres metodologías simples (decisión cerrada):
  1. Múltiplo de beneficio: EBITDA × múltiplo sectorial, ajustado
     +5% por cada factor cualitativo positivo (5 factores), máx +25%.
     EBITDA y no beneficio neto: el neto depende de deuda, amortización
     y fiscalidad particular, que no reflejan el rendimiento operativo
     real del negocio. Los múltiplos sectoriales (Damodaran ajustado
     −25%) están calibrados sobre EBITDA — confirmado, sin tocar rangos.
  2. Múltiplo de facturación: facturación × factorVentas sectorial.
  3. Activos + fondo de comercio: el fondo de comercio en la versión
     pública se deriva como la media de los métodos 1 y 2 menos el valor
     de activos físicos (sin DCF ni scorecard de 12 factores, reservados
     al pro). Esta fórmula del método 3 es una propuesta razonable para
     mantener coherencia entre métodos; no estaba explícitamente cerrada
     en las decisiones — confirmar con Rubén si prefiere otro criterio.

  Resultado final: rango fijo −15% / base / +20% sobre la media de los
  tres métodos (decisión cerrada).
*/
function calcularValoracion(data) {
  const sector = data.sector || "Otro";
  const ebitdaDeclarado = parseFloat(data.ebitda) || 0;
  const facturacion = parseFloat(data.facturacion) || 0;
  const activosFisicos = parseFloat(data.activosFisicos) || 0;

  // Ajuste por sueldo de mercado del propietario (autónomos): si el
  // negocio depende operativamente del dueño (factor "funcionaSinDueno"
  // = false) y se ha indicado un sueldo de mercado, se resta del EBITDA
  // declarado para que sea comparable con negocios que ya pagan un
  // sueldo de gestión dentro de sus gastos operativos. Si el negocio ya
  // funciona sin el dueño, se asume que ese coste ya está reflejado en
  // el EBITDA declarado y no se resta de nuevo (decisión cerrada con Rubén).
  const sueldoMercado = parseFloat(data.sueldoMercado) || 0;
  const aplicaAjusteSueldo = sueldoMercado > 0 && !data.funcionaSinDueno;
  const ebitda = aplicaAjusteSueldo ? Math.max(ebitdaDeclarado - sueldoMercado, 0) : ebitdaDeclarado;

  const multiploBase = mid(SECTORES[sector].multiploBeneficio);

  const factoresPositivos = [
    data.alquilerLargo,
    data.carteraRecurrente,
    data.funcionaSinDueno,
    data.marcaConsolidada,
    data.contratosLargoPlazo,
  ].filter(Boolean).length;

  const ajustePct = Math.min(factoresPositivos * 0.05, 0.25);
  const multiploAjustado = multiploBase * (1 + ajustePct);

  const valor1 = ebitda * multiploAjustado;
  const valor2 = facturacion * factorVentas(sector);
  const fondoComercioEstimado = Math.max((valor1 + valor2) / 2 - activosFisicos, 0);
  const valor3 = activosFisicos + fondoComercioEstimado;

  const central = (valor1 + valor2 + valor3) / 3;

  return {
    multiploBase,
    multiploAjustado,
    ajustePct,
    factoresPositivos,
    ebitdaDeclarado,
    ebitdaNormalizado: ebitda,
    sueldoMercado,
    aplicaAjusteSueldo,
    desglose: { valor1, valor2, valor3 },
    valorMinimo: central * 0.85,
    valorRecomendado: central,
    valorAspiracional: central * 1.20,
  };
}

function formatoEUR(n) {
  return Math.round(n).toLocaleString("es-ES") + " €";
}

/* ----------------------- BACKEND (Google Sheets compartido) ----------------------- */

const URL_APPS_SCRIPT = "https://script.google.com/macros/s/AKfycbzZqbnv-U0IjpXjq0PrOKTZmiSJac6Z1CQp1wLP6MFZFRigrzwLVrjY4jYqNgYLmU7Bkg/exec";
const TOKEN_SECRETO = "rly_8f3k2m9x_2026";
const URL_PRIVACIDAD = "https://www.relayable.es/politica-de-privacidad";

async function enviarLead(evento, data) {
  // TODO: función enviarGuiaSL() pendiente en el script de Apps Script.
  // Cuando llega evento "solicitud_guia_sl", el backend debe disparar un
  // email automático al usuario (remitente relayable@relayable.app) con la
  // guía completa en PDF como adjunto, además del aviso interno habitual
  // a Relayable. Esta función la implementa quien tenga acceso al backend
  // compartido (recomendablemente Rubén, o el chat de viabilidad) — este
  // frontend ya envía correctamente el evento y los datos necesarios.
  try {
    await fetch(URL_APPS_SCRIPT, {
      method: "POST",
      headers: { "Content-Type": "text/plain;charset=utf-8" }, // evita preflight CORS
      body: JSON.stringify({
        token: TOKEN_SECRETO,
        producto: "valoracion",
        evento,
        ...data,
        id_registro: data.idRegistro, // alinea con el nombre que espera el backend (bug ronda 9)
      }),
    });
    return true;
  } catch (e) {
    console.error("Error guardando lead:", e);
    return false;
  }
}

function generarIdRegistro() {
  const ahora = new Date();
  const fecha = ahora.toISOString().slice(0, 10).replace(/-/g, "");
  const hora = ahora.toISOString().slice(11, 19).replace(/:/g, "");
  const random = Math.floor(Math.random() * 900 + 100);
  return `REL-${fecha}-${hora}-${random}`;
}

function validarEmail(email) {
  if (!email) return null;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}
function validarTelefono(tel) {
  if (!tel) return null;
  const limpio = tel.replace(/[\s\-()]/g, "");
  return /^(\+?\d{1,3})?\d{9}$/.test(limpio);
}

/* ----------------------- BIFURCACIÓN: VALORAR LA SOCIEDAD COMPLETA -----------------------
   El motor de cálculo valora la unidad productiva (negocio en marcha), no
   la sociedad mercantil completa (participaciones, deuda, activos no
   afectos). Esta rama no añade un segundo motor de cálculo — es contenido
   divulgativo + captación de lead cualificado, centrado en SL.

   TEASER_SL: versión intermedia definitiva (ronda 6) — recupera el
   párrafo de documentación del comprador respecto a la versión corta de
   la ronda 4, sin volver a los listados con viñetas de la versión larga
   original. Sustituye a ambas versiones anteriores.

   CONTENIDO PENDIENTE DE RUBÉN (entregado en paralelo, no bloquea el front):
   - Texto del email automático (asunto + cuerpo) */

const TEASER_SL = [
  "Si tu negocio opera como SL, hay una distinción que marca toda la diferencia: no es lo mismo vender tu negocio que vender tu empresa.",
  "Cuando alguien valora \"el negocio\" — la actividad, la clientela, el local, el equipo — está mirando lo que genera ingresos día a día. Cuando valora \"la empresa\" como sociedad mercantil, está mirando algo más amplio: todo lo que esa sociedad tiene y todo lo que esa sociedad debe.",
  "Dos negocios idénticos en facturación y clientela pueden tener un valor muy distinto como sociedad, según la deuda pendiente (préstamos, Seguridad Social, proveedores) y los activos no afectos a la actividad (caja acumulada, inversiones, inmuebles sin uso).",
  "Vender una sociedad tampoco es como vender un negocio sin más: las participaciones se transmiten ante notario, normalmente respetando el derecho de los demás socios a comprarlas primero, y el cambio se inscribe en el Registro Mercantil.",
  "Antes de poner un precio sobre la mesa, cualquier comprador con experiencia te va a pedir: las cuentas anuales de los últimos 2-3 ejercicios, el detalle de la deuda actual, si hay contingencias pendientes (inspecciones, litigios, avales) y cómo está repartido el capital entre los socios si son varios.",
  "Esta calculadora valora tu negocio como actividad — el fondo de comercio, el equipo, la clientela. No incorpora deuda, activos no afectos ni estructura societaria, porque eso requiere mirar el balance completo de la empresa.",
];

// El teaser (TEASER_SL) ya es contenido definitivo. El subtítulo del CTA
// sigue pendiente de entrega — no confundir ambos al revisar este archivo.
const CTA_SL_SUBTITULO = "No tienes que resolver esto solo. Te mandamos la guía a tu email para que avances con tranquilidad — y seguimos aquí para lo que necesites.";

/* ----------------------- NIVELES DE INTENCIÓN DEL VENDEDOR -----------------------
   Contenido definitivo (ronda 6) — mismo estatus cerrado que C1/C2/C3 y
   E1/E2/E3 del simulador de viabilidad. Ya no es propuesta de partida. */
const NIVELES_CTA = {
  V1: {
    titular: "¿Quieres saber cuánto podría valer tu negocio?",
    subtitulo: "No es solo un número — es entender qué has construido realmente. Eso lo necesitas saber tú, vendas o no.",
    placeholder: "Cuéntanos qué te gustaría saber (opcional)",
    boton: "Quiero más información",
  },
  V2: {
    titular: "¿Quieres que te ayudemos a prepararlo para la venta?",
    subtitulo: "Esto no se vende dos veces. Antes de hablar con nadie, conviene saber qué tienes entre manos — y qué tipo de comprador es compatible con lo que has construido.",
    placeholder: "Cuéntanos en qué plazo te gustaría vender (opcional)",
    boton: "Quiero que me ayudéis a prepararlo",
  },
  V3: {
    titular: "¿Quieres que te ayudemos a cerrar el mejor acuerdo con el comprador adecuado?",
    subtitulo: "Tengas ya alguien en mente o no, el precio es solo una parte. Te ayudamos a que el acuerdo tenga sentido para ti y para lo que has construido.",
    placeholder: "Cuéntanos en una frase tu situación (opcional)",
    boton: "Quiero que me ayudéis",
  },
  no_especificado: {
    titular: "¿Quieres que te ayudemos con el siguiente paso?",
    subtitulo: "Sea lo que sea lo que tengas en mente, esto no se decide a la ligera. Cuéntanos tu caso, sin compromiso y con total confidencialidad.",
    placeholder: "Cuéntanos en una frase tu situación (opcional)",
    boton: "Quiero hablar con Relayable",
  },
};

/* ----------------------- COMPONENTES BASE ----------------------- */

function BarraProgreso({ paso, total }) {
  return (
    <div>
      <p style={{ fontSize: 11, color: C.stone, fontFamily: FONT_BODY, margin: "0 0 6px" }}>
        Paso {paso + 1} de {total}
      </p>
      <div style={{ height: 5, background: C.creamDark, borderRadius: 4, overflow: "hidden" }}>
        <div
          style={{
            height: "100%",
            background: C.ocre,
            borderRadius: 4,
            width: `${((paso + 1) / total) * 100}%`,
            transition: "width .2s",
          }}
        />
      </div>
    </div>
  );
}

function Campo({ label, hint, tooltip, children }) {
  const [showTip, setShowTip] = useState(false);
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <label style={{ fontSize: 13, color: C.stone, fontFamily: FONT_BODY }}>{label}</label>
        {tooltip && (
          <button
            type="button"
            onClick={() => setShowTip(!showTip)}
            aria-label={showTip ? "Cerrar ayuda" : "Mostrar ayuda"}
            style={{
              width: 18,
              height: 18,
              borderRadius: "50%",
              background: showTip ? C.carbon : C.ocre,
              color: C.white,
              border: "none",
              cursor: "pointer",
              fontSize: 10,
              flexShrink: 0,
            }}
          >
            {showTip ? "✕" : "?"}
          </button>
        )}
      </div>
      {tooltip && showTip && (
        <div
          style={{
            background: C.cream,
            border: `1px solid ${C.borderMid}`,
            borderRadius: 8,
            padding: "10px 12px",
            marginTop: 6,
            marginBottom: 10,
            fontSize: 12,
            fontFamily: FONT_BODY,
          }}
        >
          <p style={{ margin: 0 }}>{tooltip}</p>
          <p
            style={{
              margin: "8px 0 0",
              paddingTop: 8,
              borderTop: `1px solid ${C.borderMid}`,
              color: C.stone,
              fontSize: 11,
              fontStyle: "italic",
            }}
          >
            Si no estás seguro, pon tu mejor estimación — podrás ajustarlo más adelante.
          </p>
        </div>
      )}
      {hint && <p style={{ fontSize: 11, color: C.stone, marginTop: 4 }}>{hint}</p>}
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%",
  border: `1px solid ${C.borderMid}`,
  borderRadius: 9,
  padding: "9px 11px",
  fontSize: 13,
  fontFamily: FONT_BODY,
  color: C.carbon,
  boxSizing: "border-box",
  background: C.white,
};

function ToggleFactor({ label, tooltip, checked, onChange }) {
  const [showTip, setShowTip] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${C.border}`, padding: "10px 0" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontFamily: FONT_BODY }}>
          {label}
          <button
            type="button"
            onClick={() => setShowTip(!showTip)}
            aria-label={showTip ? "Cerrar ayuda" : "Mostrar ayuda"}
            style={{
              width: 17,
              height: 17,
              borderRadius: "50%",
              background: showTip ? C.carbon : C.ocre,
              color: C.white,
              border: "none",
              cursor: "pointer",
              fontSize: 10,
            }}
          >
            {showTip ? "✕" : "?"}
          </button>
        </div>
        <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      </div>
      {showTip && (
        <div
          style={{
            background: C.cream,
            border: `1px solid ${C.borderMid}`,
            borderRadius: 8,
            padding: "9px 11px",
            marginTop: 8,
            fontSize: 12,
            fontFamily: FONT_BODY,
          }}
        >
          {tooltip}
        </div>
      )}
    </div>
  );
}

function NivelCard({ seleccionado, onClick, children }) {
  return (
    <div
      onClick={onClick}
      style={{
        border: `1px solid ${seleccionado ? C.ocre : C.borderMid}`,
        background: seleccionado ? C.ocreLight : C.white,
        borderRadius: 10,
        padding: "10px 12px",
        marginBottom: 8,
        cursor: "pointer",
        fontSize: 13,
        fontFamily: FONT_BODY,
      }}
    >
      {children}
    </div>
  );
}

function BotonPrimario({ children, onClick, disabled, style }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: disabled ? C.creamDark : C.ocre,
        color: disabled ? C.stone : C.white,
        border: "none",
        borderRadius: 9,
        padding: "10px 20px",
        fontSize: 13,
        fontFamily: FONT_BODY,
        fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function BotonSecundario({ children, onClick, disabled, style }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: disabled ? C.creamDark : C.white,
        color: disabled ? C.stone : C.carbon,
        border: `1px solid ${C.borderMid}`,
        borderRadius: 9,
        padding: "10px 20px",
        fontSize: 13,
        fontFamily: FONT_BODY,
        cursor: disabled ? "not-allowed" : "pointer",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

/* ----------------------- PANTALLAS ----------------------- */

function FilaSL({ onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "11px 14px",
        background: C.cream,
        border: `1px solid ${C.border}`,
        borderRadius: 9,
        cursor: "pointer",
      }}
    >
      <div
        style={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          background: C.creamDark,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          fontSize: 13,
          color: C.stone,
        }}
      >
        🏢
      </div>
      <div style={{ fontSize: 12.5, color: C.carbon, flex: 1, lineHeight: 1.4, fontFamily: FONT_BODY }}>
        ¿Necesitas valorar tu <b style={{ fontWeight: 500 }}>sociedad completa</b> en vez de la actividad?
      </div>
      <div style={{ fontSize: 12, color: C.ocreDark, flexShrink: 0, fontWeight: 500, fontFamily: FONT_BODY }}>
        Ver guía →
      </div>
    </div>
  );
}

function EnlaceSL({ onSolicitarSL }) {
  return (
    <p style={{ fontSize: 11, color: C.stoneLight, textAlign: "center", marginTop: 14, fontFamily: FONT_BODY }}>
      ¿Necesitas valorar tu sociedad en vez de la actividad?{" "}
      <button
        type="button"
        onClick={onSolicitarSL}
        style={{
          background: "none",
          border: "none",
          color: C.stone,
          fontSize: 11,
          textDecoration: "underline",
          cursor: "pointer",
          padding: 0,
          fontFamily: FONT_BODY,
        }}
      >
        Haz clic aquí
      </button>
    </p>
  );
}

function PantallaBienvenida({ onContinuar, data, setData }) {
  const [mostrarSL, setMostrarSL] = useState(false);
  return (
    <div>
      <h1 style={{ fontFamily: FONT_DISPLAY, fontWeight: 500, fontSize: 24, color: C.carbon, margin: "0 0 14px" }}>
        ¿Cuánto vale tu negocio?
      </h1>
      <p style={{ fontSize: 14, color: C.stone, lineHeight: 1.7, fontFamily: FONT_BODY }}>
        En unos minutos obtendrás un rango de valoración orientativo con tres metodologías
        distintas, listo para argumentar frente a un comprador.
      </p>

      <div style={{ background: C.cream, borderRadius: 10, padding: "14px 16px", margin: "16px 0" }}>
        <p style={{ fontSize: 13, fontWeight: 500, margin: "0 0 6px", fontFamily: FONT_BODY }}>
          Lo que vas a obtener
        </p>
        <p style={{ fontSize: 13, color: C.stone, margin: 0, fontFamily: FONT_BODY }}>
          Precio mínimo de salida, precio recomendado de mercado y precio aspiracional para tu negocio.
        </p>
      </div>
      <div style={{ background: C.cream, borderRadius: 10, padding: "14px 16px" }}>
        <p style={{ fontSize: 13, fontWeight: 500, margin: "0 0 6px", fontFamily: FONT_BODY }}>
          Qué necesitas tener a mano
        </p>
        <p style={{ fontSize: 13, color: C.stone, margin: 0, fontFamily: FONT_BODY }}>
          Facturación y beneficio del negocio antes de impuestos y préstamos (EBITDA) del último
          año, y una idea aproximada del valor de tus equipos y mobiliario.
        </p>
      </div>
      <p style={{ fontSize: 12, color: C.stone, marginTop: 18, fontFamily: FONT_BODY }}>
        5 minutos · tus datos no se comparten con terceros
      </p>
      <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
        <BotonPrimario onClick={onContinuar}>Empezar →</BotonPrimario>
      </div>

      <div style={{ borderTop: `1px solid ${C.border}`, margin: "20px 0 16px" }} />
      <p style={{ fontSize: 12, color: C.stone, fontStyle: "italic", marginBottom: 14, fontFamily: FONT_BODY }}>
        Esta valoración es de tu negocio como actividad — no de la sociedad mercantil si la
        tienes constituida.
      </p>
      {!mostrarSL ? (
        <FilaSL onClick={() => setMostrarSL(true)} />
      ) : (
        <BloqueGuiaSL
          data={data}
          setData={setData}
          onCerrar={() => setMostrarSL(false)}
          textoCerrar="← Ocultar"
        />
      )}
    </div>
  );
}

function PantallaPerfil({ data, setData, onContinuar, onAtras, onSolicitarSL }) {
  const puedeAvanzar = !!data.sector;
  return (
    <div>
      <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 500, fontSize: 19, margin: "0 0 16px" }}>
        Perfil del negocio
      </h2>
      <p style={{ fontSize: 11, color: C.stoneLight, margin: "0 0 16px", fontFamily: FONT_BODY }}>
        <span style={{ color: C.red }}>*</span> Campo obligatorio — el resto son opcionales, pero
        cuantos más completes, más preciso será tu resultado.
      </p>
      <Campo label={<>Sector <span style={{ color: C.red }}>*</span></>}>
        <select
          style={inputStyle}
          value={data.sector || ""}
          onChange={(e) => setData({ ...data, sector: e.target.value })}
        >
          <option value="">Selecciona un sector</option>
          {Object.keys(SECTORES).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </Campo>
      <Campo label="Antigüedad del negocio">
        <input
          style={inputStyle}
          placeholder="ej. 8 años"
          value={data.antiguedad || ""}
          onChange={(e) => setData({ ...data, antiguedad: e.target.value })}
        />
      </Campo>
      <Campo label="Local">
        <select
          style={inputStyle}
          value={data.local || ""}
          onChange={(e) => setData({ ...data, local: e.target.value })}
        >
          <option value="">Selecciona una opción</option>
          <option value="alquilado">Alquilado</option>
          <option value="propio">En propiedad</option>
        </select>
      </Campo>
      <Campo label="Número de empleados">
        <input
          style={inputStyle}
          placeholder="ej. 3"
          value={data.empleados || ""}
          onChange={(e) => setData({ ...data, empleados: e.target.value })}
        />
      </Campo>

      <p style={{ fontSize: 13, color: C.stone, margin: "20px 0 8px", fontFamily: FONT_BODY }}>
        ¿En qué momento estás? (opcional)
      </p>
      <NivelCard seleccionado={data.nivel === "V1"} onClick={() => setData({ ...data, nivel: "V1" })}>
        Tengo curiosidad, no estoy pensando en vender ahora
      </NivelCard>
      <NivelCard seleccionado={data.nivel === "V2"} onClick={() => setData({ ...data, nivel: "V2" })}>
        Estoy considerando vender en los próximos meses
      </NivelCard>
      <NivelCard seleccionado={data.nivel === "V3"} onClick={() => setData({ ...data, nivel: "V3" })}>
        Quiero vender ya y necesito saber por cuánto
      </NivelCard>

      <Navegacion onAtras={onAtras} onContinuar={onContinuar} puedeAvanzar={puedeAvanzar}
        motivo="Selecciona el sector de tu negocio para continuar" />
      <EnlaceSL onSolicitarSL={onSolicitarSL} />
    </div>
  );
}

function PantallaFinancieros({ data, setData, onContinuar, onAtras, onSolicitarSL }) {
  const puedeAvanzar = !!data.facturacion && !!data.ebitda;
  return (
    <div>
      <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 500, fontSize: 19, margin: "0 0 16px" }}>
        Datos financieros
      </h2>
      <p style={{ fontSize: 11, color: C.stoneLight, margin: "0 0 16px", fontFamily: FONT_BODY }}>
        <span style={{ color: C.red }}>*</span> Campo obligatorio — el resto son opcionales, pero
        cuantos más completes, más preciso será tu resultado.
      </p>
      <div
        style={{
          background: C.ocreLight,
          color: C.ocreDark,
          fontSize: 12,
          padding: "9px 12px",
          borderRadius: 9,
          marginBottom: 16,
          fontFamily: FONT_BODY,
        }}
      >
        Introduce siempre los importes sin IVA. Si tu cliente te paga 121 €, introduce 100 €.
      </div>
      <Campo label={<>Facturación anual <span style={{ color: C.red }}>*</span></>}>
        <input
          style={inputStyle}
          placeholder="ej. 180000"
          value={data.facturacion || ""}
          onChange={(e) => setData({ ...data, facturacion: e.target.value.replace(/[^0-9.]/g, "") })}
        />
      </Campo>
      <Campo
        label={<>Beneficio del negocio antes de impuestos y préstamos (EBITDA) <span style={{ color: C.red }}>*</span></>}
        tooltip="Es el beneficio que genera tu negocio antes de pagar intereses de préstamos, impuestos y antes de contar el desgaste de tus equipos o maquinaria (amortización). Si no tienes este dato exacto, una buena aproximación es: ingresos − todos los gastos operativos del negocio, sin restar todavía intereses, impuestos ni amortización."
      >
        <input
          style={inputStyle}
          placeholder="ej. 24000"
          value={data.ebitda || ""}
          onChange={(e) => setData({ ...data, ebitda: e.target.value.replace(/[^0-9.]/g, "") })}
        />
      </Campo>

      <div
        style={{
          background: C.ocreLight,
          color: C.ocreDark,
          fontSize: 12,
          padding: "9px 12px",
          borderRadius: 9,
          marginBottom: 12,
          lineHeight: 1.5,
          fontFamily: FONT_BODY,
        }}
      >
        ⚠️ <b style={{ fontWeight: 500 }}>Si eres autónomo:</b> tu EBITDA normalmente ya incluye lo
        que tú mismo te llevas del negocio, porque no te pagas un sueldo aparte. Para que esta
        cifra sea comparable con la de otros negocios, indica abajo cuánto pagarías a alguien si
        tuvieras que contratarlo para hacer tu trabajo — lo restamos automáticamente. Ejemplo: si
        tu EBITDA es 30.000 € y pagarías 18.000 €/año a alguien para llevar el día a día, indica
        18.000 € abajo, no hace falta que hagas tú la resta.
      </div>
      <Campo
        label="Sueldo de mercado de tu puesto (opcional)"
        tooltip="Lo que pagarías a alguien si tuvieras que contratarlo para hacer tu trabajo en el negocio. Si no lo rellenas, asumimos que tu EBITDA ya refleja correctamente el coste de gestión."
      >
        <input
          style={inputStyle}
          placeholder="ej. 18000"
          value={data.sueldoMercado || ""}
          onChange={(e) => setData({ ...data, sueldoMercado: e.target.value.replace(/[^0-9.]/g, "") })}
        />
      </Campo>

      <Campo
        label="Valor de activos físicos"
        tooltip="Maquinaria, mobiliario y equipos a valor de mercado actual, no al precio al que los compraste."
      >
        <input
          style={inputStyle}
          placeholder="ej. 15000"
          value={data.activosFisicos || ""}
          onChange={(e) => setData({ ...data, activosFisicos: e.target.value.replace(/[^0-9.]/g, "") })}
        />
      </Campo>
      <p style={{ fontSize: 11, color: C.stone, lineHeight: 1.6, marginTop: -6, marginBottom: 18, fontFamily: FONT_BODY }}>
        Incluye solo los activos afectos a la actividad (los que usas para trabajar). No cuentes
        aquí activos personales, inversiones ajenas al negocio, ni la estructura de deudas de la
        sociedad si la tienes constituida.
      </p>

      <Navegacion onAtras={onAtras} onContinuar={onContinuar} puedeAvanzar={puedeAvanzar}
        motivo="Completa facturación y EBITDA para continuar" />
      <EnlaceSL onSolicitarSL={onSolicitarSL} />
    </div>
  );
}

function PantallaCualitativos({ data, setData, onContinuar, onAtras, onSolicitarSL }) {
  return (
    <div>
      <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 500, fontSize: 19, margin: "0 0 4px" }}>
        Factores cualitativos
      </h2>
      <p style={{ fontSize: 12, color: C.stone, margin: "0 0 14px", fontFamily: FONT_BODY }}>
        Estos factores ajustan tu valoración al alza.
      </p>
      <ToggleFactor
        label="Contrato de alquiler largo o ventajoso"
        tooltip="Más de 3 años restantes o renta por debajo de mercado en tu zona."
        checked={data.alquilerLargo}
        onChange={(v) => setData({ ...data, alquilerLargo: v })}
      />
      <ToggleFactor
        label="Cartera de clientes recurrente"
        tooltip="Clientes que vuelven a comprarte de forma habitual, sin un contrato firmado de por medio. Si tu negocio trabaja con contratos a largo plazo, ese caso se recoge en el factor de abajo, no en este."
        checked={data.carteraRecurrente}
        onChange={(v) => setData({ ...data, carteraRecurrente: v })}
      />
      <ToggleFactor
        label="El negocio funciona sin el dueño"
        tooltip="El día a día no depende de que tú estés presente cada jornada."
        checked={data.funcionaSinDueno}
        onChange={(v) => setData({ ...data, funcionaSinDueno: v })}
      />
      <ToggleFactor
        label="Marca o reputación online consolidada"
        tooltip="Buenas valoraciones, presencia en redes sociales o buscadores."
        checked={data.marcaConsolidada}
        onChange={(v) => setData({ ...data, marcaConsolidada: v })}
      />
      <ToggleFactor
        label="Más del 70% de la facturación viene de contratos firmados a más de 1 año"
        tooltip="Aplica sobre todo a negocios de servicios con clientes empresariales — limpieza, mantenimiento, distribución. Si tu negocio es de cara al público (bar, tienda, peluquería) probablemente no te aplique."
        checked={data.contratosLargoPlazo}
        onChange={(v) => setData({ ...data, contratosLargoPlazo: v })}
      />
      <Navegacion onAtras={onAtras} onContinuar={onContinuar} puedeAvanzar={true} />
      <EnlaceSL onSolicitarSL={onSolicitarSL} />
    </div>
  );
}

function PantallaRegistro({ data, setData, onContinuar, onAtras, onSolicitarSL }) {
  const emailOk = validarEmail(data.email);
  const telOk = validarTelefono(data.telefono);
  const contactoOk = emailOk === true || telOk === true;
  const puedeAvanzar = !!data.nombre && contactoOk && !!data.aceptaLegal;

  return (
    <div>
      <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 500, fontSize: 19, margin: "0 0 4px" }}>
        Antes de ver tu resultado
      </h2>
      <p style={{ fontSize: 12, color: C.stone, margin: "0 0 12px", fontFamily: FONT_BODY }}>
        Te enviamos el informe completo a tu email.
      </p>
      <p style={{ fontSize: 11, color: C.stoneLight, margin: "0 0 16px", fontFamily: FONT_BODY }}>
        <span style={{ color: C.red }}>*</span> Necesitamos tu nombre y uno de los dos contactos
        (email o teléfono), no es obligatorio rellenar los dos.
      </p>
      <Campo label={<>Nombre <span style={{ color: C.red }}>*</span></>}>
        <input
          style={inputStyle}
          placeholder="Tu nombre"
          value={data.nombre || ""}
          onChange={(e) => setData({ ...data, nombre: e.target.value })}
        />
      </Campo>
      <Campo label="Email" hint={data.email && emailOk === false ? "Revisa el formato del email" : null}>
        <input
          style={inputStyle}
          placeholder="tu@email.com"
          value={data.email || ""}
          onChange={(e) => setData({ ...data, email: e.target.value })}
        />
      </Campo>
      <Campo label="Teléfono" hint={data.telefono && telOk === false ? "Revisa el formato del teléfono" : null}>
        <input
          style={inputStyle}
          placeholder="600 000 000"
          value={data.telefono || ""}
          onChange={(e) => setData({ ...data, telefono: e.target.value })}
        />
      </Campo>
      <p style={{ fontSize: 11, color: C.stone, marginTop: -6, marginBottom: 14, fontFamily: FONT_BODY }}>
        Necesitamos email o teléfono, no es obligatorio rellenar los dos.
      </p>
      <label style={{ display: "flex", alignItems: "flex-start", gap: 7, fontSize: 11, color: C.stone, fontFamily: FONT_BODY }}>
        <input
          type="checkbox"
          checked={!!data.aceptaLegal}
          onChange={(e) => setData({ ...data, aceptaLegal: e.target.checked })}
          style={{ marginTop: 2 }}
        />
        He leído y acepto la{" "}
        <a
          href={URL_PRIVACIDAD}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{ color: C.ocreDark, textDecoration: "underline" }}
        >
          política de privacidad
        </a>
        .
      </label>
      <Navegacion onAtras={onAtras} onContinuar={onContinuar} puedeAvanzar={puedeAvanzar}
        motivo="Completa nombre, un medio de contacto y acepta la política de privacidad"
        textoBoton="Ver mi resultado →" />
      <EnlaceSL onSolicitarSL={onSolicitarSL} />
    </div>
  );
}

function PantallaResultado({ data, setData, resultado, onModificar, onNuevo }) {
  const [comentario, setComentario] = useState(data.comentario || "");
  const [enviado, setEnviado] = useState(!!data.solicitudEnviada);
  const niveles = NIVELES_CTA[data.nivel] || NIVELES_CTA.no_especificado;

  const factoresActivosLabel = [
    data.alquilerLargo && "contrato de alquiler ventajoso",
    data.carteraRecurrente && "cartera de clientes recurrente",
    data.funcionaSinDueno && "negocio que funciona sin el dueño",
    data.marcaConsolidada && "marca consolidada",
    data.contratosLargoPlazo && "contratos a largo plazo firmados",
  ].filter(Boolean);

  async function handleSolicitar() {
    setEnviado(true);
    setData({ ...data, comentario, solicitudEnviada: true });
    await enviarLead("solicitud_valoracion", { ...data, comentario, nivel: data.nivel || "no_especificado" });
  }

  return (
    <div>
      <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 500, fontSize: 20, margin: "0 0 2px" }}>
        Tu rango de valoración
      </h2>
      <p style={{ fontFamily: "monospace", fontSize: 10, color: C.stone, margin: "0 0 16px" }}>
        {data.idRegistro}
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 10, marginBottom: 16 }}>
        <ZonaPrecio label="Mínimo de salida" valor={resultado.valorMinimo} />
        <ZonaPrecio label="Recomendado" valor={resultado.valorRecomendado} destacado />
        <ZonaPrecio label="Aspiracional" valor={resultado.valorAspiracional} />
      </div>

      <div style={{ border: `1px solid ${C.borderMid}`, borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
        <p style={{ fontSize: 13, fontWeight: 500, margin: "0 0 6px", fontFamily: FONT_BODY }}>
          De dónde sale tu ajuste
        </p>
        <p style={{ fontSize: 13, color: C.stone, margin: 0, lineHeight: 1.6, fontFamily: FONT_BODY }}>
          Tu múltiplo base por sector era {resultado.multiploBase.toFixed(1)}x el EBITDA.{" "}
          {factoresActivosLabel.length > 0 ? (
            <>Por tener {factoresActivosLabel.join(", ")}, sube a {resultado.multiploAjustado.toFixed(1)}x
              ({"+"}{Math.round(resultado.ajustePct * 100)}%).</>
          ) : (
            <>No se han marcado factores cualitativos, así que se mantiene en {resultado.multiploAjustado.toFixed(1)}x.</>
          )}
        </p>
        {resultado.aplicaAjusteSueldo && (
          <p style={{ fontSize: 13, color: C.stone, margin: "8px 0 0", lineHeight: 1.6, fontFamily: FONT_BODY }}>
            Además, tu EBITDA declarado ({formatoEUR(resultado.ebitdaDeclarado)}) se ha ajustado a{" "}
            {formatoEUR(resultado.ebitdaNormalizado)} restando el sueldo de mercado de tu puesto,
            para que sea comparable con negocios que ya pagan a alguien por ese trabajo.
          </p>
        )}
      </div>

      <div style={{ background: C.cream, borderRadius: 10, padding: "12px 14px", marginBottom: 20 }}>
        <p style={{ fontSize: 12, color: C.stone, margin: "0 0 8px", lineHeight: 1.6, fontFamily: FONT_BODY }}>
          Este rango es una estimación basada en benchmarks generales del sector, no en
          transacciones reales de tu zona. El precio final de un traspaso depende también de
          factores muy locales — competencia cercana, demanda específica de tu barrio, urgencia
          del comprador — que esta calculadora no puede conocer.
        </p>
        <p style={{ fontSize: 12, color: C.stone, margin: 0, lineHeight: 1.6, fontFamily: FONT_BODY }}>
          En Relayable trabajamos con datos de transacciones reales para ajustar valoraciones con
          mayor precisión.
        </p>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        <BotonSecundario onClick={onModificar} disabled={enviado} style={{ flex: 1 }}>
          Modificar mis datos
        </BotonSecundario>
        <BotonSecundario onClick={onNuevo} disabled={enviado} style={{ flex: 1 }}>
          Nuevo análisis
        </BotonSecundario>
      </div>

      <div style={{ background: C.carbon, borderRadius: 12, padding: 16 }}>
        <p style={{ fontFamily: FONT_DISPLAY, color: C.white, fontSize: 15, margin: "0 0 5px" }}>
          {niveles.titular}
        </p>
        <p style={{ fontSize: 12, color: C.stoneLight, margin: "0 0 12px", fontFamily: FONT_BODY }}>
          {niveles.subtitulo}
        </p>
        <textarea
          disabled={enviado}
          value={comentario}
          onChange={(e) => {
            if (e.target.value.length <= 600) setComentario(e.target.value);
          }}
          placeholder={niveles.placeholder}
          rows={2}
          style={{
            width: "100%",
            background: "#26231F",
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: 8,
            padding: "9px 11px",
            fontSize: 12,
            color: C.white,
            resize: "none",
            boxSizing: "border-box",
            fontFamily: FONT_BODY,
          }}
        />
        <p style={{ fontSize: 10, color: comentario.length > 540 ? C.red : "#5F5E5A", textAlign: "right", margin: "4px 0 10px" }}>
          {comentario.length} / 600
        </p>
        <BotonPrimario onClick={handleSolicitar} disabled={enviado} style={{ width: "100%", background: enviado ? C.green : C.ocre }}>
          {enviado ? "✓ Solicitud enviada — te contactaremos" : niveles.boton}
        </BotonPrimario>
        {enviado && (
          <>
            <p style={{ fontSize: 10, color: C.stoneLight, marginTop: 8, textAlign: "center", fontFamily: "monospace" }}>
              Referencia: {data.idRegistro}
            </p>
            <a href="https://www.relayable.es/" target="_blank" rel="noopener noreferrer" style={{ display: "block", marginTop: 14, textDecoration: "none" }}>
              <BotonPrimario style={{ width: "100%" }}>Ir a la web Relayable</BotonPrimario>
            </a>
          </>
        )}
      </div>
    </div>
  );
}

function BloqueGuiaSL({ data, setData, onCerrar, textoCerrar }) {
  const [comentarioSL, setComentarioSL] = useState(data.comentarioSL || "");
  const [enviado, setEnviado] = useState(!!data.solicitudGuiaSLEnviada);

  const emailOk = validarEmail(data.email);
  const telOk = validarTelefono(data.telefono);
  const contactoOk = emailOk === true || telOk === true;
  const puedeEnviar = !!data.nombre && contactoOk && !!data.aceptaLegal;

  async function handleSolicitar() {
    if (!puedeEnviar) return;
    setEnviado(true);
    setData({ ...data, comentarioSL, solicitudGuiaSLEnviada: true });
    // El backend debe disparar el email automático con la guía PDF adjunta
    // al recibir este evento. Ver TODO junto a enviarLead más abajo.
    await enviarLead("solicitud_guia_sl", { ...data, comentarioSL });
  }

  return (
    <div>
      <button
        type="button"
        onClick={onCerrar}
        style={{ background: "none", border: "none", color: C.stone, fontSize: 12, cursor: "pointer", padding: 0, marginBottom: 14, fontFamily: FONT_BODY }}
      >
        {textoCerrar || "← Volver a valorar mi negocio"}
      </button>

      <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 500, fontSize: 19, margin: "0 0 14px" }}>
        Valorar tu sociedad limitada
      </h2>

      {TEASER_SL.map((parrafo, i) => (
        <p key={i} style={{ fontSize: 13, color: C.stone, lineHeight: 1.7, fontFamily: FONT_BODY, marginBottom: 12 }}>
          {parrafo}
        </p>
      ))}

      <div style={{ background: C.carbon, borderRadius: 12, padding: 16, marginTop: 18 }}>
        <p style={{ fontFamily: FONT_DISPLAY, color: C.white, fontSize: 15, margin: "0 0 5px" }}>
          ¿Quieres la guía completa?
        </p>
        <p style={{ fontSize: 12, color: C.stoneLight, margin: "0 0 14px", fontFamily: FONT_BODY }}>
          {CTA_SL_SUBTITULO}
        </p>

        {!enviado && (
          <>
            <input
              placeholder="Tu nombre"
              value={data.nombre || ""}
              onChange={(e) => setData({ ...data, nombre: e.target.value })}
              style={{ ...inputStyle, marginBottom: 8, background: "#26231F", color: C.white, border: "1px solid rgba(255,255,255,0.15)" }}
            />
            <input
              placeholder="tu@email.com"
              value={data.email || ""}
              onChange={(e) => setData({ ...data, email: e.target.value })}
              style={{ ...inputStyle, marginBottom: 8, background: "#26231F", color: C.white, border: "1px solid rgba(255,255,255,0.15)" }}
            />
            <input
              placeholder="600 000 000"
              value={data.telefono || ""}
              onChange={(e) => setData({ ...data, telefono: e.target.value })}
              style={{ ...inputStyle, marginBottom: 8, background: "#26231F", color: C.white, border: "1px solid rgba(255,255,255,0.15)" }}
            />
            <p style={{ fontSize: 10, color: C.stoneLight, margin: "0 0 10px", fontFamily: FONT_BODY }}>
              Necesitamos email o teléfono, no es obligatorio rellenar los dos.
            </p>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 7, fontSize: 11, color: C.stoneLight, fontFamily: FONT_BODY, marginBottom: 10 }}>
              <input
                type="checkbox"
                checked={!!data.aceptaLegal}
                onChange={(e) => setData({ ...data, aceptaLegal: e.target.checked })}
                style={{ marginTop: 2 }}
              />
              He leído y acepto la{" "}
              <a
                href={URL_PRIVACIDAD}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ color: C.ocre, textDecoration: "underline" }}
              >
                política de privacidad
              </a>
              .
            </label>

            <textarea
              value={comentarioSL}
              onChange={(e) => {
                if (e.target.value.length <= 600) setComentarioSL(e.target.value);
              }}
              placeholder="Cuéntanos en una frase tu situación (opcional)"
              rows={2}
              style={{
                width: "100%",
                background: "#26231F",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 8,
                padding: "9px 11px",
                fontSize: 12,
                color: C.white,
                resize: "none",
                boxSizing: "border-box",
                fontFamily: FONT_BODY,
              }}
            />
            <p style={{ fontSize: 10, color: comentarioSL.length > 540 ? C.red : "#5F5E5A", textAlign: "right", margin: "4px 0 10px" }}>
              {comentarioSL.length} / 600
            </p>
          </>
        )}

        <BotonPrimario
          onClick={handleSolicitar}
          disabled={enviado || !puedeEnviar}
          style={{ width: "100%", background: enviado ? C.green : C.ocre }}
        >
          {enviado ? "✓ Solicitud enviada — revisa tu email" : "Quiero la guía completa"}
        </BotonPrimario>
        {!enviado && !puedeEnviar && (
          <p style={{ fontSize: 11, color: C.red, marginTop: 6, fontFamily: FONT_BODY }}>
            Completa nombre, un medio de contacto y acepta la política de privacidad.
          </p>
        )}
        {enviado && (
          <>
            <p style={{ fontSize: 10, color: C.stoneLight, marginTop: 8, textAlign: "center", fontFamily: "monospace" }}>
              Referencia: {data.idRegistro}
            </p>
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <BotonSecundario
                onClick={onCerrar}
                style={{ flex: 1, background: "transparent", border: "1px solid rgba(255,255,255,0.3)", color: C.white }}
              >
                Continuar con el simulador
              </BotonSecundario>
              <a href="https://www.relayable.es/" target="_blank" rel="noopener noreferrer" style={{ flex: 1, textDecoration: "none" }}>
                <BotonPrimario style={{ width: "100%" }}>Ir a la web Relayable</BotonPrimario>
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ZonaPrecio({ label, valor, destacado }) {
  return (
    <div
      style={{
        background: destacado ? C.ocreLight : C.cream,
        border: destacado ? `1px solid ${C.ocre}` : "none",
        borderRadius: 10,
        padding: 12,
        textAlign: "center",
      }}
    >
      <p style={{ fontSize: 11, color: destacado ? C.ocreDark : C.stone, margin: "0 0 4px", fontFamily: FONT_BODY }}>
        {label}
      </p>
      <p style={{ fontSize: 18, fontWeight: 500, margin: 0, color: destacado ? C.ocreDark : C.carbon, fontFamily: FONT_BODY }}>
        {formatoEUR(valor)}
      </p>
    </div>
  );
}

function Navegacion({ onAtras, onContinuar, puedeAvanzar, motivo, textoBoton }) {
  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        {onAtras ? <BotonSecundario onClick={onAtras}>← Atrás</BotonSecundario> : <span />}
        <BotonPrimario onClick={onContinuar} disabled={!puedeAvanzar}>
          {textoBoton || "Continuar →"}
        </BotonPrimario>
      </div>
      {!puedeAvanzar && motivo && (
        <p style={{ fontSize: 11, color: C.red, textAlign: "right", marginTop: 6, fontFamily: FONT_BODY }}>
          {motivo}
        </p>
      )}
    </div>
  );
}

/* ----------------------- COMPONENTE PRINCIPAL ----------------------- */

const PASOS = ["bienvenida", "perfil", "financieros", "cualitativos", "registro", "resultado"];
const STORAGE_KEY = "relayable_valoracion_draft";

function estadoInicial() {
  try {
    const guardado = localStorage.getItem(STORAGE_KEY);
    if (guardado) return JSON.parse(guardado);
  } catch (e) {
    /* localStorage no disponible o dato corrupto, se ignora */
  }
  return { idRegistro: generarIdRegistro() };
}

export default function SimuladorValoracion({ usuario }) {
  // El prop `usuario` no se usa todavía — se reserva para la futura capa
  // de autenticación (Supabase) sin necesidad de refactor mayor.
  const [paso, setPaso] = useState(0);
  const [data, setData] = useState(estadoInicial);
  const [ramaSL, setRamaSL] = useState(false);
  const registroEnviado = useRef(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      /* almacenamiento no disponible, se ignora silenciosamente */
    }
  }, [data]);

  useEffect(() => {
    function handler(e) {
      if (paso > 0 && paso < 5) {
        e.preventDefault();
        e.returnValue = "";
      }
    }
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [paso]);

  const resultado = calcularValoracion(data);

  function irAResultado() {
    setPaso(5);
    if (!registroEnviado.current) {
      registroEnviado.current = true;
      enviarLead("registro", data);
    }
  }

  function modificarDatos() {
    setPaso(1); // vuelve al wizard conservando todos los datos
  }

  function nuevoAnalisis() {
    const nuevoId = generarIdRegistro();
    setData({ idRegistro: nuevoId });
    registroEnviado.current = false;
    setRamaSL(false);
    setPaso(0);
  }

  const pantallas = {
    bienvenida: (
      <PantallaBienvenida onContinuar={() => setPaso(1)} data={data} setData={setData} />
    ),
    perfil: (
      <PantallaPerfil
        data={data}
        setData={setData}
        onAtras={() => setPaso(0)}
        onContinuar={() => setPaso(2)}
        onSolicitarSL={() => setRamaSL(true)}
      />
    ),
    financieros: (
      <PantallaFinancieros
        data={data}
        setData={setData}
        onAtras={() => setPaso(1)}
        onContinuar={() => setPaso(3)}
        onSolicitarSL={() => setRamaSL(true)}
      />
    ),
    cualitativos: (
      <PantallaCualitativos
        data={data}
        setData={setData}
        onAtras={() => setPaso(2)}
        onContinuar={() => setPaso(4)}
        onSolicitarSL={() => setRamaSL(true)}
      />
    ),
    registro: (
      <PantallaRegistro
        data={data}
        setData={setData}
        onAtras={() => setPaso(3)}
        onContinuar={irAResultado}
        onSolicitarSL={() => setRamaSL(true)}
      />
    ),
    resultado: (
      <PantallaResultado
        data={data}
        setData={setData}
        resultado={resultado}
        onModificar={modificarDatos}
        onNuevo={nuevoAnalisis}
      />
    ),
  };

  return (
    <>
      <CargarFuentes />
      <div style={{ background: C.cream, minHeight: "100vh", padding: "32px 16px", fontFamily: FONT_BODY }}>
        <div style={{ maxWidth: 480, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <Logo />
          </div>
          {!ramaSL && paso < 5 && (
            <div style={{ marginBottom: 20 }}>
              <BarraProgreso paso={paso} total={5} />
            </div>
          )}
          <div
            style={{
              background: C.white,
              borderRadius: 14,
              padding: 24,
              border: `1px solid ${C.border}`,
            }}
          >
            {ramaSL ? (
              <BloqueGuiaSL data={data} setData={setData} onCerrar={() => setRamaSL(false)} textoCerrar="← Volver a valorar mi negocio" />
            ) : (
              pantallas[PASOS[paso]]
            )}
          </div>
        </div>
      </div>
    </>
  );
}
